import { useCallback, useEffect, useRef, useState } from 'react';
/** Checks a date against bounds and a disabled-date callback. */
export function validateIsoDate(iso, { min, max, isDateDisabled }) {
    if (min && iso < min)
        return 'before-min';
    if (max && iso > max)
        return 'after-max';
    if (isDateDisabled?.(iso))
        return 'unavailable';
    return null;
}
/**
 * The typed-text half of a date field: keeps the text the user is editing,
 * re-formats it from the value when the value changes, and on `commit()`
 * parses and validates it. Invalid text is kept (so the user can fix it) and
 * reported as `error`; the value is left unchanged.
 */
export function useDateText({ value, format, parse, validate, onCommit }) {
    const shown = value ? format(value) : '';
    const [text, setText] = useState(shown);
    const [typedError, setTypedError] = useState(null);
    const editing = useRef(false);
    useEffect(() => {
        if (editing.current)
            return;
        setText(shown);
        setTypedError(null);
    }, [shown]);
    const onTextChange = useCallback((next) => {
        editing.current = true;
        setText(next);
    }, []);
    /** Parses the text. Returns the date, `null` for empty text, or `undefined` when it is not usable. */
    const commit = () => {
        editing.current = false;
        const t = text.trim();
        if (!t) {
            setTypedError(null);
            setText('');
            if (value !== null)
                onCommit(null);
            return null;
        }
        const iso = parse(t);
        const error = iso ? validate(iso) : 'invalid';
        if (!iso || error) {
            setTypedError(error);
            return undefined;
        }
        setTypedError(null);
        setText(format(iso));
        if (iso !== value)
            onCommit(iso);
        return iso;
    };
    /** Sets a value from outside the text (calendar, preset, clear button). */
    const set = (iso) => {
        editing.current = false;
        setTypedError(null);
        setText(iso ? format(iso) : '');
        if (iso !== value)
            onCommit(iso);
    };
    /** Shows a date in the text without committing it (the caller commits the value itself). */
    const show = (iso) => {
        editing.current = false;
        setTypedError(null);
        setText(iso ? format(iso) : '');
    };
    const valueError = value ? validate(value) : null;
    return { text, onTextChange, commit, set, show, error: typedError ?? valueError };
}
//# sourceMappingURL=use-date-text.js.map